import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import SwiperCore, { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.scss';
import translator from 'utils/translator';
import useResponsiveQuery from 'utils/useResponsiveQuery';
import { LinkWithQuery } from 'components/common/LinkWithQuery';
import { FIGMA_TO_STORYCARDS_ROUTE, STUDIO_ROUTE } from 'components/constants';

import { LazyVideo } from '../../../../common/LazyVideo';
import styles from './styles.module.scss';

const _t = (path: string) => translator.text(`homepage.${path}`);

SwiperCore.use([Autoplay]);

// Set to false to restore the previous inline slider placement above the video.
const STUDIO_SLIDER_ON_VIDEO_FADE = true;

const tabsContent: Array<{ title: string; text: string; videoSrc: string; videoSrcMob: string; videoPoster?: string }> =
  [
    {
      title: _t('designContent.tabsContent.0.title'),
      text: _t('designContent.tabsContent.0.text'),
      videoSrc: 'https://assts.stories.sc/website/desktop/design.mp4',
      videoSrcMob: 'https://assts.stories.sc/website/mobile/design.mp4',
      videoPoster: 'https://assts.stories.sc/website/poster/design.jpg',
    },
    {
      title: _t('designContent.tabsContent.1.title'),
      text: _t('designContent.tabsContent.1.text'),
      videoSrc: 'https://assts.stories.sc/website/desktop/content.mp4',
      videoSrcMob: 'https://assts.stories.sc/website/mobile/content.mp4',
      videoPoster: 'https://assts.stories.sc/website/poster/content.jpg',
    },
  ];

const studioFeatures = [
  {
    title: 'Figma integration',
    text: 'Connect Figma designs with element comments. Storycards AI turns the instructions into fully editable behavior.',
    href: FIGMA_TO_STORYCARDS_ROUTE,
  },
  {
    title: 'AI chat, side by side',
    text: 'Give design instructions in the chat and see every change appear live in the studio.',
  },
  {
    title: 'Precision at every breakpoint',
    text: 'Control layout and content across desktop, tablet and mobile.',
  },
  {
    title: 'Advanced motion & interactions',
    text: 'Build states, Lottie animations and action-based triggers.',
  },
  {
    title: 'Reusable design systems',
    text: 'Create components once and update them globally across every product.',
  },
  {
    title: 'Brand rules & approved templates',
    text: 'Define reusable starting points and protected rules for every team.',
  },
  {
    title: 'Protected editing',
    text: 'Lock brand-critical layouts, styles and interactions while teams update approved fields.',
  },
  {
    title: 'Controlled publishing',
    text: 'Decide who can edit, review and publish every experience.',
  },
  {
    title: 'Revision history',
    text: 'Review every change and restore earlier versions whenever your team needs to.',
  },
];

function DesignContent() {
  const isMobile = useResponsiveQuery();
  const sliderSectionRef = useRef<HTMLDivElement>(null);
  const studioSwiperRef = useRef<SwiperCore | null>(null);

  useEffect(() => {
    const sliderSection = sliderSectionRef.current;
    if (!sliderSection) return undefined;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const observer = new IntersectionObserver(
      ([entry]) => {
        const studioSwiper = studioSwiperRef.current;
        if (!studioSwiper?.autoplay) return;

        if (entry.isIntersecting && !reducedMotion.matches) studioSwiper.autoplay.start();
        else studioSwiper.autoplay.stop();
      },
      { threshold: 0.3 },
    );

    observer.observe(sliderSection);

    return () => {
      observer.disconnect();
      studioSwiperRef.current?.autoplay?.stop();
    };
  }, []);

  const renderTab = ({
    title,
    text,
    videoLink,
    videoLinkMobile,
    videoPoster,
  }: {
    title: string;
    text: string;
    videoLink: string;
    videoLinkMobile: string;
    videoPoster?: string;
  }) => {
    return (
      <div
        className={classNames(styles.tabContent, {
          [styles.sliderOnVideoFade]: STUDIO_SLIDER_ON_VIDEO_FADE,
        })}
      >
        <div className={styles.text}>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.descriptionRow}>
            <p>{text}</p>
            <LinkWithQuery className={styles.studioCta} to={STUDIO_ROUTE}>
              <span>Explore the studio</span>
              <svg aria-hidden="true" viewBox="0 0 18 18">
                <path d="M3.75 9h10.5M10 4.75 14.25 9 10 13.25" />
              </svg>
            </LinkWithQuery>
          </div>
          <div
            className={styles.studioSliderSection}
            ref={sliderSectionRef}
            role="region"
            aria-label="Studio capabilities and design governance"
          >
            <Swiper
              className={styles.studioSwiper}
              loop
              grabCursor
              speed={650}
              spaceBetween={0}
              slidesPerView={1.12}
              autoplay={{ delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true }}
              breakpoints={{
                640: { slidesPerView: 2.18 },
                1024: { slidesPerView: 3.3 },
              }}
              onSwiper={(studioSwiper) => {
                studioSwiperRef.current = studioSwiper;
                studioSwiper.autoplay.stop();
              }}
            >
              {studioFeatures.map((feature, index) => (
                <SwiperSlide className={styles.studioFeatureSlide} key={feature.title}>
                  <article>
                    <span className={styles.featureNumber}>{String(index + 1).padStart(2, '0')}</span>
                    <div className={styles.featureCopy}>
                      <strong>{feature.title}</strong>
                      <span className={styles.featureText}>{feature.text}</span>
                      {feature.href && (
                        <LinkWithQuery className={styles.featureLink} to={feature.href}>
                          See Figma workflow →
                        </LinkWithQuery>
                      )}
                    </div>
                  </article>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className={styles.sliderControls}>
              <button
                type="button"
                aria-label="Next studio capability"
                onClick={() => studioSwiperRef.current?.slideNext()}
              >
                <svg aria-hidden="true" viewBox="0 0 18 18">
                  <path d="M6.75 4.75 11 9l-4.25 4.25" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className={styles.videoWrapper}>
          <LazyVideo
            src={isMobile ? videoLinkMobile : videoLink}
            poster={videoPoster}
            autoplay
            className={styles.video}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={classNames(styles.designContent, 'flex flex-col w-full items-center')}>
      <div className={classNames(styles.content, 'flex flex-col')}>
        <div className={styles.tabsContent}>
          {renderTab({
            title: tabsContent[0].title,
            text: tabsContent[0].text,
            videoLink: tabsContent[0].videoSrc,
            videoLinkMobile: tabsContent[0].videoSrcMob,
            videoPoster: tabsContent[0].videoPoster,
          })}
        </div>
      </div>
    </div>
  );
}

export default DesignContent;
